import React, { useState, useEffect, useContext, useCallback } from "react"
import {
    Text,
    View,
    StyleSheet,
    ScrollView,
} from "react-native"

import { AuthContext } from '../../contexts/auth/Store';

import { AnalyticsService } from "../../constants/Analytics";
import Colors from "../../constants/Colors";
import ProductService from "../../services/ProductService";
import GenericHeader from '../../components/headers/GenericHeader';
import config from "../../constants/Config";
import { GetPricetag, SafeJSONObject } from "../../constants/Utils";
import { unionBy } from "lodash";
import BrandService from "../../services/BrandService";
import { IProduct, IProductPagedData } from "../../interfaces/product";
import { IBrand } from "../../interfaces/brand";
import { useFocusEffect, useNavigation, useNavigationState, useRoute } from "@react-navigation/native";
import ProductStack from "../../components/product/ProductStack";
import AsyncStorage from "@react-native-async-storage/async-storage";
import NextPageLoaderGeneric from "../../components/common/NextPageLoaderGeneric";

const analytics = new AnalyticsService();
const productService = new ProductService();
const brandService = new BrandService();

function BrandProductScreen() {
    const route = useRoute();
    const navigation = useNavigation();

    const auth: any = useContext(AuthContext)
    const buyer = auth.getUserData.buyerData

    const [isLoading, setLoading] = useState(true);
    const [loadingNext, setLoadingNext] = useState(false)

    const [brand, setBrand]: [IBrand | null, any] = useState(null)
    const [state, setState]: [IProductPagedData, any] = useState({
        page: 0,
        limit: config.pagination_limit,
        total: 1,
        count: 0,
        data: []
    })

    const fetch_brand_products = async (_id: string) => {
        let _buyer = await AsyncStorage.getItem('buyerData');
        _buyer = SafeJSONObject(_buyer)
        if (!state.data.length) {
            setLoading(true)
        }
        if (state.page < state.total) {
            productService.fetchAvailableByBrand(
                _id,
                _buyer.location_id,
                state.page + 1,
                state.limit
            )
                .then((res: IProductPagedData) => {
                    res.data = res.data.map((product: IProduct) => {
                        let pricetag = GetPricetag(product)
                        if (pricetag) {
                            product['pricetag'] = pricetag
                            return product
                        }
                        return null
                    }).filter(product => product)

                    setState((prev: IProductPagedData) => {
                        return {
                            ...res,
                            data: unionBy(prev.data, res.data, '_id')
                        }
                    })
                    setLoading(false)
                    setLoadingNext(false)
                })
                .catch(err => {
                    setLoading(false)
                    setLoadingNext(false)
                })
        } else {
            setLoading(false)
            setLoadingNext(false)
        }
    }

    const fetchNextPage = () => {
        setLoadingNext(true)
        if (brand) {
            fetch_brand_products(brand._id)
        }
    }

    const initialize = (id: string) => {
        brandService
            .fetchById(id)
            .then(res => {
                setBrand(res)

                analytics.logEvent('Viewed Brand Products', auth.getUserData.buyerData, { brand_data: res })
                analytics.googleHelpers.setCurrentScreen('ViewedBrandProducts', 'ViewedBrandProducts')
                
                setState((prev: IProductPagedData) => {
                    return {
                        ...prev,
                        // data: [{ _id: 'brand_001', upload: { path: '2020/11/default' }, name: `All ${res.name} Products` }]
                    }
                })
                fetch_brand_products(id)
                navigation.setOptions({
                    title: res.name + ' Products - Suplias Now'
                })
            })
            .catch(err => {
                nav_back()
            })
    }

    const nav_back = () => {
        if (brand) {
            //
            navigation.navigate('ManufacturerBrand', {
                id: brand.manufacturer._id
            })
        } else {
            navigation.navigate('HomeTab', {
                screen: 'Manufacturer'
            })
        }
    }

    useFocusEffect(
        useCallback(() => {
            // the screen is focused, take actions
            if (!route.params?.id) {
                nav_back()
            } else {
                initialize(route.params?.id)
            }

            return () => {
                // screen is blurred, take actions
                setBrand(null)
                setLoading(true)
                setState({
                    page: 0,
                    limit: config.pagination_limit,
                    total: 1,
                    count: 0,
                    data: []
                })
            };
        }, [route])
    );


    return <>
        <GenericHeader
            title={`${brand && !isLoading ? brand.name + ' Products' : 'Brand Products'}`}
            backAction={nav_back}
        />
        <View style={styles.container}>
            <ScrollView contentContainerStyle={[
                styles.scrollView
            ]}>
                <ProductStack
                    products={state.data}
                    isLoading={isLoading}
                    showHeading={true}
                    onEndReached={fetchNextPage}
                    footerComponent={() => <NextPageLoaderGeneric loading={loadingNext} page={state.page} total={state.total} action={fetchNextPage} />}
                />
            </ScrollView>
        </View>
    </>
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'flex-start',
    },
    scrollView: {
        flexGrow: 1,
        height: '100%',
        backgroundColor: Colors.shades._3,
    }
})

export default BrandProductScreen
